import React, { useState, useEffect } from 'react';
import { 
  Smartphone, 
  Upload, 
  Edit, 
  Trash2, 
  Plus, 
  Download,
  Monitor,
  Globe,
  FileText,
  Calendar,
  Save,
  X,
  AlertCircle,
  Copy,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { apiService } from '../services/api';

interface AppVersion {
  _id: string;
  version: string;
  file_path: string;
  file_size: number;
  upload_date: string;
  download_count: number;
  is_active: boolean;
  short_url?: string;
}

interface App {
  _id: string;
  name: string;
  description: string;
  platforms: string[];
  icon_url?: string;
  website_url?: string;
  download_code: string;
  is_active: boolean;
  versions: AppVersion[];
  createdAt: string;
  updatedAt: string;
}

const AppManagement: React.FC = () => {
  const [apps, setApps] = useState<App[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingApp, setEditingApp] = useState<App | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Função auxiliar para garantir que ID seja sempre string
  // Trata ObjectId do MongoDB, objetos com $oid, e outros casos
  const ensureStringId = (id: any): string => {
    // Se não há ID, retornar string vazia (validação será feita depois)
    if (!id) {
      return '';
    }
    // Se já é string válida, retornar diretamente
    if (typeof id === 'string') {
      // Verificar se não é [object Object] disfarçado
      if (id === '[object Object]' || id.includes('[object')) {
        return '';
      }
      return id;
    }
    // Se for objeto com $oid (formato MongoDB JSON Extended)
    if (typeof id === 'object' && id.$oid) {
      return String(id.$oid);
    }
    // Se for objeto com _id interno
    if (typeof id === 'object' && id._id) {
      return ensureStringId(id._id);
    }
    // Se for objeto com id interno
    if (typeof id === 'object' && id.id) {
      return ensureStringId(id.id);
    }
    // Se tiver método toString que não seja [object Object]
    if (typeof id === 'object' && typeof id.toString === 'function') {
      const strValue = id.toString();
      // Verificar se toString retornou algo útil (não [object Object])
      if (strValue && strValue !== '[object Object]' && !strValue.includes('[object')) {
        return strValue;
      }
    }
    // Última tentativa: JSON.stringify e extrair valor
    try {
      const jsonStr = JSON.stringify(id);
      // Se for formato { "$oid": "xxx" }
      const oidMatch = jsonStr.match(/"?\$oid"?\s*:\s*"([^"]+)"/);
      if (oidMatch) {
        return oidMatch[1];
      }
      // Tentar extrair _id como string
      const idMatch = jsonStr.match(/"_id"\s*:\s*"([^"]+)"/);
      if (idMatch) {
        return idMatch[1];
      }
    } catch (e) {
      // Ignorar erros de stringify
    }
    // NUNCA retornar [object Object] - retornar string vazia
    const finalStr = String(id);
    if (finalStr === '[object Object]' || finalStr.includes('[object')) {
      return '';
    }
    return finalStr;
  };

  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    platforms: [] as string[],
    website_url: '',
    icon_url: '',
    download_code: ''
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [newVersion, setNewVersion] = useState('');

  const platformOptions = [
    { value: 'android', label: 'Android', icon: Smartphone },
    { value: 'ios', label: 'iOS', icon: Monitor },
    { value: 'windows', label: 'Windows', icon: Monitor },
    { value: 'macos', label: 'macOS', icon: Monitor },
    { value: 'samsung', label: 'Samsung TV', icon: Monitor },
    { value: 'lg', label: 'LG TV', icon: Monitor },
    { value: 'web', label: 'Web', icon: Globe }
  ];

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getAppManagement();

      // A resposta pode vir como { success: true, data: [...] } ou diretamente como { data: [...] }
      let appsArray: any[] = [];
      
      if (response?.success && Array.isArray(response.data)) {
        appsArray = response.data;
      } else if (Array.isArray(response.data)) {
        appsArray = response.data;
      } else if (Array.isArray(response)) {
        appsArray = response;
      }


      // Função local para converter ID de forma segura (não lança erro)
      const safeExtractId = (id: any): string => {
        if (!id) return '';
        if (typeof id === 'string') return id;
        // Se for objeto com $oid (formato MongoDB JSON Extended)
        if (typeof id === 'object' && id.$oid) return String(id.$oid);
        // Se for objeto com _id interno
        if (typeof id === 'object' && id._id) return safeExtractId(id._id);
        // Se for objeto com id interno
        if (typeof id === 'object' && id.id) return safeExtractId(id.id);
        // Tentar toString se não for [object Object]
        if (typeof id === 'object' && typeof id.toString === 'function') {
          const strValue = id.toString();
          if (strValue && strValue !== '[object Object]' && !strValue.includes('[object')) {
            return strValue;
          }
        }
        // Última tentativa: JSON.stringify e extrair $oid ou qualquer ID
        try {
          const jsonStr = JSON.stringify(id);
          // Tentar extrair $oid
          const oidMatch = jsonStr.match(/"?\$oid"?\s*:\s*"([^"]+)"/);
          if (oidMatch) return oidMatch[1];
          // Tentar extrair _id como string
          const idMatch = jsonStr.match(/"_id"\s*:\s*"([^"]+)"/);
          if (idMatch) return idMatch[1];
        } catch (e) {}
        // NUNCA retornar [object Object] - retornar string vazia
        const finalStr = String(id);
        if (finalStr === '[object Object]' || finalStr.includes('[object')) {
          return '';
        }
        return finalStr;
      };

      // Garantir que todos os apps tenham as propriedades versions e platforms como arrays
      // E converter _id para string sempre usando extração segura
      const appsData = appsArray.map((app: any) => {
        // Converter _id para string de forma segura
        const appId = safeExtractId(app._id);
        
        // Processar versões e converter seus IDs também
        let processedVersions: any[] = [];
        if (Array.isArray(app.versions)) {
          processedVersions = app.versions.map((version: any) => {
            const versionId = safeExtractId(version._id);
            return {
              ...version,
              _id: versionId
            };
          });
        }
        
        const processedApp = {
          ...app,
          _id: appId, // Garantir que _id seja sempre string
          versions: processedVersions,
          platforms: Array.isArray(app.platforms) ? app.platforms : []
        };
        
        return processedApp;
      });
      
      setApps(appsData);
    } catch (error: any) {
      setApps([]); // Garantir que apps seja sempre um array
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlatformToggle = (platform: string) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || formData.platforms.length === 0) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Se está editando, atualizar código se mudou
      if (editingApp) {
        // Validar dados antes de enviar
        if (!formData.name || !formData.name.trim()) {
          alert('Nome do aplicativo é obrigatório');
          setIsUploading(false);
          return;
        }
        
        if (!formData.description || !formData.description.trim()) {
          alert('Descrição é obrigatória');
          setIsUploading(false);
          return;
        }
        
        if (!formData.platforms || formData.platforms.length === 0) {
          alert('Pelo menos uma plataforma deve ser selecionada');
          setIsUploading(false);
          return;
        }
        
        // Atualizar informações básicas do app
        // Se houver um novo arquivo, enviar via FormData
        if (selectedFile && newVersion) {
          // Validar versão
          if (!newVersion.trim()) {
            alert('Versão é obrigatória ao enviar um novo arquivo');
            setIsUploading(false);
            return;
          }
          
          // Obter ID do app e validar
          const appIdString = ensureStringId(editingApp._id);
          if (!appIdString || appIdString === '[object Object]') {
            alert('Erro: ID do aplicativo não encontrado. Recarregue a página e tente novamente.');
            setIsUploading(false);
            return;
          }
          
          // Enviar via FormData para incluir o arquivo
          const formDataToSend = new FormData();
          formDataToSend.append('name', formData.name.trim());
          formDataToSend.append('description', formData.description.trim());
          formDataToSend.append('platforms', JSON.stringify(formData.platforms));
          formDataToSend.append('website_url', formData.website_url?.trim() || '');
          formDataToSend.append('icon_url', formData.icon_url?.trim() || '');
          formDataToSend.append('file', selectedFile);
          formDataToSend.append('version', newVersion.trim());
          
          try {
            await apiService.updateAppWithFile(appIdString, formDataToSend);
          } catch (updateError: any) {
            const errorMessage = updateError.response?.data?.error || 
                                 updateError.response?.data?.details?.[0]?.msg ||
                                 updateError.message || 
                                 'Erro ao atualizar aplicativo';
            alert(`Erro ao atualizar aplicativo: ${errorMessage}`);
            setIsUploading(false);
            return;
          }
        } else {
          // Obter ID do app e validar
          const appIdString = ensureStringId(editingApp._id);
          if (!appIdString || appIdString === '[object Object]') {
            alert('Erro: ID do aplicativo não encontrado. Recarregue a página e tente novamente.');
            setIsUploading(false);
            return;
          }
          
          // Atualizar apenas dados básicos (sem arquivo)
          try {
            await apiService.updateApp(appIdString, {
              name: formData.name.trim(),
              description: formData.description.trim(),
              platforms: JSON.stringify(formData.platforms),
              website_url: formData.website_url?.trim() || null,
              icon_url: formData.icon_url?.trim() || null
            });
          } catch (updateError: any) {
            const errorMessage = updateError.response?.data?.error || 
                                 updateError.response?.data?.details?.[0]?.msg ||
                                 updateError.message || 
                                 'Erro ao atualizar aplicativo';
            alert(`Erro ao atualizar aplicativo: ${errorMessage}`);
            setIsUploading(false);
            return;
          }
        }
        
        // Se o código mudou, atualizar separadamente
        if (formData.download_code && formData.download_code !== editingApp.download_code) {
          const codeAppIdString = ensureStringId(editingApp._id);
          if (!codeAppIdString || codeAppIdString === '[object Object]') {
            alert('Erro: ID do aplicativo não encontrado para atualizar código.');
            setIsUploading(false);
            return;
          }
          try {
            await apiService.updateAppDownloadCode(codeAppIdString, formData.download_code);
          } catch (err: any) {
            alert(err.response?.data?.error || 'Erro ao atualizar código');
            setIsUploading(false);
            return;
          }
        }
        
        alert(selectedFile && newVersion ? 'Aplicativo e versão atualizados com sucesso!' : 'Aplicativo atualizado com sucesso!');
        setShowUploadModal(false);
        setEditingApp(null);
        setSelectedFile(null);
        setNewVersion('');
        setFormData({
          name: '',
          description: '',
          platforms: [],
          website_url: '',
          icon_url: '',
          download_code: ''
        });
        fetchApps();
      } else {
        // Criando novo app
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('platforms', JSON.stringify(formData.platforms));
        formDataToSend.append('website_url', formData.website_url);
        formDataToSend.append('icon_url', formData.icon_url);
        
        if (selectedFile) {
          formDataToSend.append('file', selectedFile);
          formDataToSend.append('version', newVersion);
        }

        // Simular progresso de upload
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return prev;
            }
            return prev + 10;
          });
        }, 200);

        const response = await apiService.uploadApp(formDataToSend);
        
        clearInterval(progressInterval);
        setUploadProgress(100);

        if (response.success) {
          alert(`Aplicativo criado com sucesso!\n\nCódigo de Acesso: ${response.download_code}\n\nCompartilhe este código com seus clientes para download.`);
          setShowUploadModal(false);
          setFormData({
            name: '',
            description: '',
            platforms: [],
            website_url: '',
            icon_url: '',
            download_code: ''
          });
          setSelectedFile(null);
          setNewVersion('');
          setUploadProgress(0);
          fetchApps();
        }
      }
    } catch (error) {
      alert('Erro ao salvar aplicativo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditApp = (app: App) => {
    // Garantir que o ID seja string antes de definir como editingApp
    const appWithStringId = {
      ...app,
      _id: ensureStringId(app._id),
      versions: app.versions?.map((v: any) => ({
        ...v,
        _id: v._id ? ensureStringId(v._id) : v._id
      })) || []
    };
    
    setEditingApp(appWithStringId);
    setFormData({
      name: app.name,
      description: app.description,
      platforms: app.platforms,
      website_url: app.website_url || '',
      icon_url: app.icon_url || '',
      download_code: app.download_code || ''
    });
    setShowUploadModal(true);
  };

  const handleDeleteApp = async (appId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este aplicativo?')) {
      try {
        await apiService.deleteApp(appId);
        alert('Aplicativo excluído com sucesso!');
        fetchApps();
      } catch (error) {
        alert('Erro ao excluir aplicativo');
      }
    }
  };

  const handleToggleAppStatus = async (appId: string, currentStatus: boolean) => {
    try {
      await apiService.toggleAppStatus(appId);
      alert(`Aplicativo ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`);
      fetchApps();
    } catch (error) {
      alert('Erro ao alterar status do aplicativo');
    }
  };

  const getPlatformIcon = (platform: string) => {
    const platformOption = platformOptions.find(p => p.value === platform);
    return platformOption ? platformOption.icon : Smartphone;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gerenciamento de Aplicativos</h1>
          <p className="text-sm sm:text-base text-gray-600">Gerencie os aplicativos disponíveis para download</p>
        </div>
        <Button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Novo Aplicativo
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Smartphone className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total de Apps</p>
                <p className="text-2xl font-bold text-gray-900">{apps.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Download className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Apps Ativos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {apps.filter(app => app.is_active).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <FileText className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total de Versões</p>
                <p className="text-2xl font-bold text-gray-900">
                  {apps.reduce((total, app) => total + (Array.isArray(app.versions) ? app.versions.length : 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Downloads Total</p>
                <p className="text-2xl font-bold text-gray-900">
                  {apps.reduce((total, app) => {
                    const versions = Array.isArray(app.versions) ? app.versions : [];
                    return total + versions.reduce((vTotal, version) => vTotal + (version.download_count || 0), 0);
                  }, 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Aplicativos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {apps.map((app) => (
          <Card key={app._id} className="overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {app.icon_url ? (
                    <img 
                      src={app.icon_url} 
                      alt={app.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Smartphone className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <CardTitle className="text-lg">{app.name}</CardTitle>
                    <Badge variant={app.is_active ? "default" : "secondary"}>
                      {app.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditApp(app)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const appIdString = ensureStringId(app._id);
                      if (!appIdString || appIdString === '[object Object]') {
                        alert('Erro: ID do aplicativo inválido. Recarregue a página.');
                        return;
                      }
                      handleToggleAppStatus(appIdString, app.is_active);
                    }}
                  >
                    {app.is_active ? 'Desativar' : 'Ativar'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const appIdString = ensureStringId(app._id);
                      if (!appIdString || appIdString === '[object Object]') {
                        alert('Erro: ID do aplicativo inválido. Recarregue a página.');
                        return;
                      }
                      handleDeleteApp(appIdString);
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-gray-600 mb-4">{app.description}</p>
              
              {/* Plataformas */}
              <div className="flex flex-wrap gap-2 mb-4">
                {app.platforms.map((platform) => {
                  const Icon = getPlatformIcon(platform);
                  return (
                    <Badge key={platform} variant="outline" className="flex items-center gap-1">
                      <Icon className="h-3 w-3" />
                      {platform}
                    </Badge>
                  );
                })}
              </div>

              {/* Código de Acesso */}
              {app.download_code && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-green-700 font-medium">🔒 Código de Acesso</span>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(app.download_code);
                          alert('Código copiado para a área de transferência!');
                        } catch (err) {
                          // Fallback para navegadores mais antigos
                          const textArea = document.createElement('textarea');
                          textArea.value = app.download_code;
                          textArea.style.position = 'fixed';
                          textArea.style.opacity = '0';
                          document.body.appendChild(textArea);
                          textArea.select();
                          try {
                            document.execCommand('copy');
                            alert('Código copiado para a área de transferência!');
                          } catch (fallbackErr) {
                            alert('Erro ao copiar. Tente selecionar o texto manualmente.');
                          }
                          // Evita NotFoundError intermitente (o node pode já não estar no DOM)
                          try {
                            textArea.remove();
                          } catch (_) {
                            if (textArea.parentNode) textArea.parentNode.removeChild(textArea);
                          }
                        }
                      }}
                      className="text-xs text-green-600 hover:text-green-800 font-medium flex items-center gap-1"
                    >
                      <Copy className="h-3 w-3" />
                      Copiar
                    </button>
                  </div>
                  <p 
                    className="text-center font-mono font-bold text-lg text-green-900 tracking-wider select-text cursor-text"
                    style={{ userSelect: 'text', WebkitUserSelect: 'text', MozUserSelect: 'text' } as React.CSSProperties}
                    onDoubleClick={(e) => {
                      const range = document.createRange();
                      range.selectNodeContents(e.currentTarget);
                      const selection = window.getSelection();
                      selection?.removeAllRanges();
                      selection?.addRange(range);
                    }}
                  >
                    {app.download_code}
                  </p>
                  <p className="text-xs text-green-600 text-center mt-1">
                    Clientes precisam deste código para fazer download • Clique duas vezes para selecionar
                  </p>
                </div>
              )}
              
              {/* Link de Download */}
              {(() => {
                const activeVersion = app.versions?.find(v => v.is_active);
                const shortUrl = activeVersion?.short_url;
                return (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-blue-700 font-medium">🔗 Link de Download</span>
                      <div className="flex items-center gap-2">
                        {shortUrl && (
                          <>
                            <button
                              onClick={async () => {
                                try {
                                  // Tentar usar a API moderna do clipboard
                                  if (navigator.clipboard && navigator.clipboard.writeText) {
                                    await navigator.clipboard.writeText(shortUrl);
                                    alert('Link copiado para a área de transferência!');
                                  } else {
                                    // Fallback para navegadores mais antigos
                                    const textArea = document.createElement('textarea');
                                    textArea.value = shortUrl;
                                    textArea.style.position = 'fixed';
                                    textArea.style.opacity = '0';
                                    textArea.style.left = '-999999px';
                                    textArea.style.top = '-999999px';
                                    document.body.appendChild(textArea);
                                    textArea.focus();
                                    textArea.select();
                                    
                                    try {
                                      const successful = document.execCommand('copy');
                                      // Evita NotFoundError intermitente (o node pode já não estar no DOM)
                                      try {
                                        textArea.remove();
                                      } catch (_) {
                                        if (textArea.parentNode) textArea.parentNode.removeChild(textArea);
                                      }
                                      
                                      if (successful) {
                                        alert('Link copiado para a área de transferência!');
                                      } else {
                                        throw new Error('Falha ao copiar usando execCommand');
                                      }
                                    } catch (fallbackErr) {
                                      // Evita NotFoundError intermitente (o node pode já não estar no DOM)
                                      try {
                                        textArea.remove();
                                      } catch (_) {
                                        if (textArea.parentNode) textArea.parentNode.removeChild(textArea);
                                      }
                                      // Selecionar o texto para o usuário copiar manualmente
                                      const linkElement = document.querySelector(`[data-link-url="${shortUrl}"]`) as HTMLElement;
                                      if (linkElement) {
                                        const range = document.createRange();
                                        range.selectNodeContents(linkElement);
                                        const selection = window.getSelection();
                                        selection?.removeAllRanges();
                                        selection?.addRange(range);
                                      }
                                      alert('Não foi possível copiar automaticamente. O link foi selecionado - pressione Ctrl+C para copiar.');
                                    }
                                  }
                                } catch (err: any) {
                                  // Tentar selecionar o texto como último recurso
                                  const linkElement = document.querySelector(`[data-link-url="${shortUrl}"]`) as HTMLElement;
                                  if (linkElement) {
                                    const range = document.createRange();
                                    range.selectNodeContents(linkElement);
                                    const selection = window.getSelection();
                                    selection?.removeAllRanges();
                                    selection?.addRange(range);
                                    alert('Erro ao copiar automaticamente. O link foi selecionado - pressione Ctrl+C para copiar manualmente.');
                                  } else {
                                    alert('Erro ao copiar link. Tente selecionar o texto manualmente.');
                                  }
                                }
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors"
                              title="Copiar link de download"
                            >
                              <Copy className="h-3 w-3" />
                              Copiar
                            </button>
                            <button
                              onClick={() => {
                                window.open(shortUrl, '_blank', 'noopener,noreferrer');
                              }}
                              className="text-xs text-green-600 hover:text-green-800 font-medium flex items-center gap-1 transition-colors"
                              title="Abrir link em nova aba"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Abrir
                            </button>
                          </>
                        )}
                        <button
                          onClick={async () => {
                            if (window.confirm('Gerar novo link encurtado para este aplicativo?')) {
                              try {
                                const appIdString = ensureStringId(app._id);
                                // Validar ID antes de enviar
                                if (!appIdString || appIdString === '[object Object]' || appIdString.includes('[object')) {
                                  alert('Erro: ID do aplicativo inválido. Recarregue a página e tente novamente.');
                                  return;
                                }
                                const response = await apiService.regenerateAppShortUrl(appIdString);
                                alert(`Link encurtado gerado!\n\n${response.data?.short_url || 'Link criado com sucesso'}`);
                                fetchApps();
                              } catch (err: any) {
                                const errorMsg = err.response?.data?.error || err.message || 'Erro desconhecido';
                                alert(`Erro ao gerar link:\n${errorMsg}`);
                              }
                            }
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors"
                        >
                          🔄 {shortUrl ? 'Regenerar' : 'Gerar Link'}
                        </button>
                      </div>
                    </div>
                    {shortUrl ? (
                      <div className="space-y-2">
                        <a 
                          href={shortUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-link-url={shortUrl}
                          className="block text-center font-mono text-sm text-blue-900 hover:text-blue-700 underline break-all cursor-pointer select-text bg-blue-100 p-2 rounded border border-blue-300"
                          onClick={(e) => {
                            // Permitir seleção do texto ao clicar
                            e.preventDefault();
                            const range = document.createRange();
                            range.selectNodeContents(e.currentTarget);
                            const selection = window.getSelection();
                            selection?.removeAllRanges();
                            selection?.addRange(range);
                            // Depois de um breve delay, abrir o link
                            setTimeout(() => {
                              window.open(shortUrl, '_blank');
                            }, 300);
                          }}
                          title="Clique para selecionar o link, ou mantenha pressionado Ctrl e clique para abrir"
                        >
                          {shortUrl}
                        </a>
                        <p className="text-xs text-gray-500 text-center">
                          💡 Clique no link para selecioná-lo ou mantenha Ctrl + Clique para abrir
                        </p>
                      </div>
                    ) : (
                      <p className="text-center text-xs text-orange-600">
                        ⚠️ Link encurtado não gerado. Clique em "Gerar Link"
                      </p>
                    )}
                  </div>
                );
              })()}
              
              {/* Versões */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-900">Versões:</h4>
                {Array.isArray(app.versions) && app.versions.length > 0 ? (
                  <>
                    {app.versions.slice(0, 3).map((version) => (
                      <div key={version._id} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{version.version}</Badge>
                          <span className="text-gray-500">
                            {version.file_size ? ((version.file_size / 1024 / 1024).toFixed(1)) : '0'} MB
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">{version.download_count || 0} downloads</span>
                          <Badge variant={version.is_active ? "default" : "secondary"}>
                            {version.is_active ? 'Ativa' : 'Inativa'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {app.versions.length > 3 && (
                      <p className="text-xs text-gray-500">
                        +{app.versions.length - 3} versões adicionais
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-gray-500">Nenhuma versão disponível</p>
                )}
              </div>

              {app.website_url && (
                <div className="mt-4">
                  <a 
                    href={app.website_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Globe className="h-3 w-3" />
                    Site oficial
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Modal de Upload/Criação */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">
                {editingApp ? 'Editar Aplicativo' : 'Novo Aplicativo'}
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowUploadModal(false);
                  setEditingApp(null);
                  setFormData({
                    name: '',
                    description: '',
                    platforms: [],
                    website_url: '',
                    icon_url: '',
                    download_code: ''
                  });
                  setSelectedFile(null);
                  setNewVersion('');
                  setUploadProgress(0);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Nome do App */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Aplicativo *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: IPTV Player Pro"
                  required
                  readOnly={false}
                  style={{ userSelect: 'text', WebkitUserSelect: 'text' } as React.CSSProperties}
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva o aplicativo..."
                  rows={3}
                  required
                  readOnly={false}
                  style={{ userSelect: 'text', WebkitUserSelect: 'text' } as React.CSSProperties}
                />
              </div>

              {/* Plataformas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plataformas Suportadas *
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {platformOptions.map((platform) => {
                    const Icon = platform.icon;
                    return (
                      <button
                        key={platform.value}
                        type="button"
                        onClick={() => handlePlatformToggle(platform.value)}
                        className={`flex items-center gap-2 p-3 rounded-lg border text-sm transition-colors ${
                          formData.platforms.includes(platform.value)
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {platform.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* URL do Site */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL do Site (opcional)
                </label>
                <Input
                  value={formData.website_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
                  placeholder="https://exemplo.com"
                  type="url"
                  readOnly={false}
                  style={{ userSelect: 'text', WebkitUserSelect: 'text' } as React.CSSProperties}
                />
              </div>

              {/* URL do Ícone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL do Ícone (opcional)
                </label>
                <Input
                  value={formData.icon_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, icon_url: e.target.value }))}
                  placeholder="https://exemplo.com/icon.png"
                  type="url"
                  readOnly={false}
                  style={{ userSelect: 'text', WebkitUserSelect: 'text' } as React.CSSProperties}
                />
              </div>

              {/* Código de Download (somente edição) */}
              {editingApp && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Código de Acesso ao Download
                    </label>
                    <button
                      type="button"
                      onClick={async () => {
                        if (formData.download_code) {
                          try {
                            await navigator.clipboard.writeText(formData.download_code);
                            alert('Código copiado para a área de transferência!');
                          } catch (err) {
                            // Fallback para navegadores mais antigos
                            const textArea = document.createElement('textarea');
                            textArea.value = formData.download_code;
                            textArea.style.position = 'fixed';
                            textArea.style.opacity = '0';
                            document.body.appendChild(textArea);
                            textArea.select();
                            try {
                              document.execCommand('copy');
                              alert('Código copiado para a área de transferência!');
                            } catch (fallbackErr) {
                              alert('Erro ao copiar. Tente selecionar o texto manualmente.');
                            }
                            // Evita NotFoundError intermitente (o node pode já não estar no DOM)
                            try {
                              textArea.remove();
                            } catch (_) {
                              if (textArea.parentNode) textArea.parentNode.removeChild(textArea);
                            }
                          }
                        } else {
                          alert('Não há código para copiar');
                        }
                      }}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      <Copy className="h-3 w-3" />
                      Copiar
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      value={formData.download_code}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        download_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') 
                      }))}
                      placeholder="A2B3C"
                      maxLength={5}
                      className="font-mono tracking-widest text-xl text-center select-all"
                      readOnly={false}
                      style={{ userSelect: 'text', WebkitUserSelect: 'text' } as React.CSSProperties}
                      onFocus={(e) => e.target.select()}
                    />
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                      <div className="flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        <span className="font-medium">Importante:</span>
                      </div>
                      <p className="mt-1">Clientes precisarão deste código para fazer download. Compartilhe com cuidado!</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Upload de Arquivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Arquivo do Aplicativo {!editingApp && '*'}
                </label>
                {editingApp && (
                  <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                    <div className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      <span className="font-medium">Ao enviar um novo arquivo:</span>
                    </div>
                    <p className="mt-1">A versão anterior será removida e substituída pela nova versão.</p>
                  </div>
                )}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    accept=".apk,.ipa,.exe,.dmg,.zip"
                    className="hidden"
                    id="file-upload"
                    required={!editingApp}
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <div className="text-center">
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">
                        {selectedFile ? selectedFile.name : editingApp ? 'Clique para selecionar um novo arquivo (opcional)' : 'Clique para selecionar um arquivo'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        APK, IPA, EXE, DMG, ZIP
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Versão */}
              {selectedFile && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Versão *
                  </label>
                  <Input
                    value={newVersion}
                    onChange={(e) => setNewVersion(e.target.value)}
                    placeholder="Ex: 1.0.0"
                    required
                    readOnly={false}
                    style={{ userSelect: 'text', WebkitUserSelect: 'text' } as React.CSSProperties}
                  />
                </div>
              )}

              {/* Progresso do Upload */}
              {isUploading && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-gray-600">Fazendo upload...</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500">{uploadProgress}% concluído</p>
                </div>
              )}

              {/* Botões */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowUploadModal(false)}
                  disabled={isUploading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isUploading || (!selectedFile && !editingApp)}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingApp ? 'Salvar Alterações' : 'Criar Aplicativo'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppManagement;
